import React, { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BrandHeader from "../BrandHeader";
import { useAuth } from "../../hooks/useAuth";
import { trackEventOnce } from "../../lib/analytics";

export default function CheckoutSuccessPage() {
  const { t } = useTranslation("auth");
  const { user, loading } = useAuth();
  const [params] = useSearchParams();

  // Stripe returns here with ?session_id=cs_... (see backend
  // stripe_service.success_url). Keying the conversion on that id makes the
  // event idempotent: a refresh or a shared link never counts twice.
  // Money numbers are deliberately absent — the charged amount and currency
  // live in the Stripe webhook (Adaptive Pricing means the client cannot know
  // them), so GA4 counts conversions here and Stripe stays the revenue truth.
  useEffect(() => {
    const sessionId = params.get("session_id");
    trackEventOnce(`purchase_${sessionId || "nosession"}`, "purchase", {
      transaction_id: sessionId || undefined,
    });
  }, [params]);

  return (
    <div className="min-h-screen bg-[#050505]">
      <BrandHeader />
      <div className="flex items-center justify-center min-h-[calc(100vh-6rem)] px-6">
        <div className="w-full max-w-md feature-card p-8 rounded-lg text-center">
          <div className="text-4xl mb-4">&#10003;</div>
          <h1 className="font-clash text-2xl font-bold text-white mb-4">{t("checkoutSuccess.heading")}</h1>
          <p className="text-[#A1A1AA] mb-6">{t("checkoutSuccess.body")}</p>
          {/* Only for someone who bought while signed in. A guest buyer has an
              account but no password yet, so this button would send them to a
              login screen they cannot pass — the worst possible next step
              right after paying. Their way in is the email, which the copy
              above points at. Hidden while auth is still resolving, so it
              never flashes in and out. */}
          {!loading && user && (
            <Link to="/dashboard" className="btn-primary inline-block px-8 py-3 rounded text-sm font-semibold">{t("checkoutSuccess.dashboardButton")}</Link>
          )}
          {/* GDPR / LCEN L34-5: inform customers at collection time that we may
              use their email for marketing similar products. Soft opt-in is set
              on the webhook; this notice + the unsubscribe link in every email
              satisfies the "informed at collection" requirement. */}
          <p className="mt-8 text-[11px] leading-relaxed text-[#A1A1AA]/70">
            {t("checkoutSuccess.marketingNoticePrefix")}{" "}
            <Link to="/dashboard/settings" className="underline hover:text-[#FF5500]">{t("checkoutSuccess.accountSettings")}</Link>{t("checkoutSuccess.marketingNoticeSuffix")}
          </p>
        </div>
      </div>
    </div>
  );
}
